import moment from "moment";
import { TableCellType } from "../../../../components/custom-table/table";
import { AuthStatusOptionModel } from "../../../../data-model/authStatusOptions";
import { MedicalPaOrder } from "../../../../data-model/medicalPaOrdersModel";
import { GenericAuthStatusTableData } from "../../../../data-model/tablesData";
import {
  DocWriteBackStatus,
  EVWriteBackStatus,
} from "../../../../enums/evBvWriteBackStatus";
import { MedicalPaOrdersAuthStatus } from "../../../../enums/medicalPaOrdersAuthStatus";
import { GenericAuthStatusTableColumnKeys } from "../../../../enums/tableColumnKeys";
import { generateDataForDocWithSideNavModalFromSingleMedicalPaOrder } from "../../../../utils/createDataForDocWithSideNavModal";
import { generateFullName } from "../../../../utils/generateOrderDataForTable";
import { getAuthStatusForTables } from "../../../../utils/getAuthStatusOfMedicalPaOrder";
import { rotateLastSegmentFirst } from "../../../../utils/getFullNameOfTheProvider";
import { getScreenshotValues } from "../../../../utils/getScreenshotValues";
import { capitalizeString } from "../../../../utils/stringModifications";
import { findLabelFromGivenState } from "../../../pharmaPaForm/utils/stateOptions";

export const getTableHeaders = (tabID: string) => {
  if (tabID === "pending" || tabID === "hold") {
    return [
      {
        label: "Patient Name",
        key: GenericAuthStatusTableColumnKeys.PATIENT_DETAILS_WITH_LEFT_BORDER,
        order: 1,
        width: 12,
        sortable: true,
        type: TableCellType.MULTILINE,
        subKey: "mainText",
      },
      {
        label: "Date of Work",
        key: GenericAuthStatusTableColumnKeys.DATE_OF_WORK,
        order: 2,
        width: 10,
        sortable: true,
        type: TableCellType.DATE,
      },
      {
        label: "Plan Details",
        key: GenericAuthStatusTableColumnKeys.PLAN_DETAILS,
        order: 3,
        width: 15,
        sortable: true,
        type: TableCellType.MULTILINE,
        subKey: "mainText",
      },
      {
        label: "Medication",
        key: GenericAuthStatusTableColumnKeys.MEDICATION,
        order: 4,
        width: 10,
        sortable: true,
        type: TableCellType.STRING,
      },
      {
        label: "Provider",
        key: GenericAuthStatusTableColumnKeys.PROVIDER,
        order: 5,
        width: 12,
        sortable: true,
        type: TableCellType.STRING,
      },
      {
        label: "DOS",
        key: GenericAuthStatusTableColumnKeys.DATE_OF_SERVICE,
        order: 6,
        width: 8,
        sortable: true,
        type: TableCellType.DATE,
      },
      {
        label: "BO Value",
        key: GenericAuthStatusTableColumnKeys.BO_VALUE,
        order: 7,
        width: 10,
        type: TableCellType.BADGE,
      },
      tabID === "pending"
        ? {
            label: "Last Checked",
            key: GenericAuthStatusTableColumnKeys.LAST_CHECKED,
            order: 8,
            width: 13,
            type: TableCellType.STRING,
          }
        : {
            label: "Hold Until",
            key: GenericAuthStatusTableColumnKeys.HOLD_UNTIL,
            order: 8,
            width: 13,
            type: TableCellType.STRING,
          },
      {
        label: "",
        key: GenericAuthStatusTableColumnKeys.RPA_STATUS,
        order: 9,
        width: 4,
        type: TableCellType.RPA_STATUS,
      },
      {
        label: "",
        key: GenericAuthStatusTableColumnKeys.RPA_STATUS_TWO,
        order: 10,
        width: tabID === "pending" ? 3 : 6,
        type: TableCellType.RPA_STATUS_TWO,
      },
      ...(tabID === "pending"
        ? [
            {
              label: "",
              key: GenericAuthStatusTableColumnKeys.SIDE_MODAL_ICON,
              order: 11,
              width: 3,
              type: TableCellType.SIDE_MODAL_ICON,
            },
          ]
        : []),
    ];
  } else {
    return [
      {
        label: "Patient Name",
        key: GenericAuthStatusTableColumnKeys.PATIENT_DETAILS,
        order: 1,
        width: 15,
        sortable: true,
        type: TableCellType.MULTILINE,
        subKey: "mainText",
      },
      {
        label: "Date of Work",
        key: GenericAuthStatusTableColumnKeys.DATE_OF_WORK,
        order: 2,
        width: 11,
        sortable: true,
        type: TableCellType.DATE,
      },
      {
        label: "Plan Details",
        key: GenericAuthStatusTableColumnKeys.PLAN_DETAILS,
        order: 3,
        width: 14,
        sortable: true,
        type: TableCellType.MULTILINE,
        subKey: "mainText",
      },
      {
        label: "Medication",
        key: GenericAuthStatusTableColumnKeys.MEDICATION,
        order: 4,
        width: 10,
        sortable: true,
        type: TableCellType.STRING,
      },
      {
        label: "Provider",
        key: GenericAuthStatusTableColumnKeys.PROVIDER,
        order: 5,
        width: 15,
        sortable: true,
        type: TableCellType.STRING,
      },
      {
        label: "DOS",
        key: GenericAuthStatusTableColumnKeys.DATE_OF_SERVICE,
        order: 6,
        width: 8,
        sortable: true,
        type: TableCellType.DATE,
      },
      {
        label: "BO Value",
        key: GenericAuthStatusTableColumnKeys.BO_VALUE,
        order: 7,
        width: 10,
        type: TableCellType.BADGE,
      },
      {
        label: "Auth Status",
        key: GenericAuthStatusTableColumnKeys.AUTH_VERIFICATION,
        order: 8,
        width: 10,
        type: TableCellType.BADGE,
      },
      {
        label: "",
        key: GenericAuthStatusTableColumnKeys.RPA_STATUS,
        order: 9,
        width: 4,
        type: TableCellType.RPA_STATUS,
      },
      {
        label: "",
        key: GenericAuthStatusTableColumnKeys.RPA_STATUS_TWO,
        order: 10,
        width: 3,
        type: TableCellType.RPA_STATUS_TWO,
      },
    ];
  }
};

export const generateGenericAuthStatusTableData = (
  medicalPaOrders: MedicalPaOrder[],
  authStatusOptions: AuthStatusOptionModel[],
): GenericAuthStatusTableData[] => {
  return medicalPaOrders.map((medicalPaOrder) => {
    const authStatus = getAuthStatusForTables(
      medicalPaOrder,
      authStatusOptions,
    );

    return {
      [GenericAuthStatusTableColumnKeys.PATIENT_DETAILS_WITH_LEFT_BORDER]: {
        mainText: capitalizeString(
          generateFullName(
            medicalPaOrder?.demographics?.first_name ?? "",
            "",
            medicalPaOrder?.demographics?.last_name ?? "",
          ),
        ),
        secondaryText: `MRN : ${medicalPaOrder?.demographics?.patient_mrn ?? ""}`,
        shouldShowLeftBorder: leftBorderShouldShow(medicalPaOrder),
        leftBorderColor: getleftBorderColorforPatientDetails(medicalPaOrder),
      },
      [GenericAuthStatusTableColumnKeys.PATIENT_DETAILS]: {
        mainText: capitalizeString(
          generateFullName(
            medicalPaOrder?.demographics?.first_name ?? "",
            "",
            medicalPaOrder?.demographics?.last_name ?? "",
          ),
        ),
        secondaryText: `MRN : ${medicalPaOrder?.demographics?.patient_mrn ?? ""}`,
      },
      [GenericAuthStatusTableColumnKeys.DATE_OF_SERVICE]: moment(
        medicalPaOrder?.payload?.date_of_service,
      ).format("MM/DD/YYYY"),
      [GenericAuthStatusTableColumnKeys.PLAN_DETAILS]: {
        mainText: medicalPaOrder?.coverage?.primary?.payer_name ?? "",
        secondaryText: medicalPaOrder?.coverage?.primary?.member_id ?? "",
      },
      [GenericAuthStatusTableColumnKeys.MEDICATION]:
        medicalPaOrder?.payload?.regimen_name ?? "",
      [GenericAuthStatusTableColumnKeys.ASSIGNEE]:
        capitalizeString(medicalPaOrder?.assigned_to?.provider_name) ?? "--",
      [GenericAuthStatusTableColumnKeys.STATE]: findLabelFromGivenState(
        medicalPaOrder?.demographics?.address?.state ?? "",
      ),
      [GenericAuthStatusTableColumnKeys.ROW_DATA]:
        JSON.stringify(medicalPaOrder),
      [GenericAuthStatusTableColumnKeys.ID]: medicalPaOrder?.id ?? "",
      [GenericAuthStatusTableColumnKeys.CREATED_AT]: moment(
        medicalPaOrder?.created_at,
      ).format("MM/DD/YYYY"),
      [GenericAuthStatusTableColumnKeys.DATE_OF_WORK]:
        medicalPaOrder?.timestamps?.date_of_work,

      [GenericAuthStatusTableColumnKeys.AUTH_VERIFICATION]: {
        text: authStatus?.text ?? "",
        color: authStatus?.textColor ?? "",
        bgColor: authStatus?.bgColor ?? "",
      },
      [GenericAuthStatusTableColumnKeys.LAST_CHECKED]: medicalPaOrder
        ?.timestamps?.date_of_last_checked
        ? moment(medicalPaOrder?.timestamps?.date_of_last_checked).format(
            "MM/DD/YYYY",
          )
        : "--",
      [GenericAuthStatusTableColumnKeys.HOLD_UNTIL]: medicalPaOrder?.timestamps
        ?.date_of_hold_until
        ? moment(medicalPaOrder?.timestamps?.date_of_hold_until).format(
            "MM/DD/YYYY",
          )
        : "--",

      [GenericAuthStatusTableColumnKeys.BO_VALUE]: {
        text: medicalPaOrder?.status?.bo_status ?? "Required",
        color: "#0f0f0f",
        bgColor: "#f0f0f0",
      },
      [GenericAuthStatusTableColumnKeys.RPA_STATUS]: {
        status:
          medicalPaOrder?.status?.ev_write_back_status ??
          EVWriteBackStatus.NOT_INITIATED,
        id: medicalPaOrder?.id ?? "",
        shouldPropagate: true,
        masterAuthStatus: medicalPaOrder?.status?.master_auth_status ?? "",
      },
      [GenericAuthStatusTableColumnKeys.RPA_STATUS_TWO]: {
        status:
          medicalPaOrder?.status?.document_upload_status ??
          DocWriteBackStatus.NOT_INITIATED,
        filePaths: getScreenshotValues(
          medicalPaOrder?.auth_on_file?.screenshots
            ?.write_note_cig_box_screenshot,
        ),
        docList:
          generateDataForDocWithSideNavModalFromSingleMedicalPaOrder(
            medicalPaOrder,
          ),
        masterAuthStatus: medicalPaOrder?.status?.master_auth_status ?? "",
      },
      [GenericAuthStatusTableColumnKeys.PROVIDER]: rotateLastSegmentFirst(
        medicalPaOrder?.payload?.practitioner_name ?? "",
      ),
      [GenericAuthStatusTableColumnKeys.SIDE_MODAL_ICON]: {
        id: medicalPaOrder?.id ?? "",
      },
    };
  });
};

export const leftBorderShouldShow = (order_data: MedicalPaOrder) => {
  if (
    order_data?.status?.master_auth_status ===
      MedicalPaOrdersAuthStatus.Pending ||
    order_data?.status?.master_auth_status === MedicalPaOrdersAuthStatus.Hold
  ) {
    return true;
  }
  return false;
};

export const getleftBorderColorforPatientDetails = (
  medicalPaOrder: MedicalPaOrder,
) => {
  if (
    medicalPaOrder?.timestamps?.date_of_last_checked &&
    medicalPaOrder?.status?.master_auth_status ===
      MedicalPaOrdersAuthStatus.Pending
  ) {
    const lastCheckedTime = moment(
      medicalPaOrder.timestamps.date_of_last_checked,
    );
    const now = moment();
    const hoursSinceLastChecked = now.diff(lastCheckedTime, "hours");

    if (hoursSinceLastChecked >= 24 && hoursSinceLastChecked < 48) {
      return "#EF6C00";
    } else if (hoursSinceLastChecked >= 48) {
      return "#CD0202";
    }
  } else if (
    medicalPaOrder?.timestamps?.date_of_hold_until &&
    medicalPaOrder?.status?.master_auth_status ===
      MedicalPaOrdersAuthStatus.Hold
  ) {
    const lastCheckedTime = moment(
      medicalPaOrder.timestamps.date_of_hold_until,
    );
    const now = moment();
    const hoursSinceLastChecked = now.diff(lastCheckedTime, "hours");

    if (hoursSinceLastChecked >= 24 && hoursSinceLastChecked < 48) {
      return "#EF6C00";
    } else if (hoursSinceLastChecked >= 48) {
      return "#CD0202";
    }
  }
  return "transparent";
};
